import { Form } from "@inertiajs/react"
import { Loader2 } from "lucide-react"
import {
  type ChangeEvent,
  type FocusEvent,
  type ReactNode,
  type RefObject,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogTrigger,
} from "@/components/responsive-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useOnline } from "@/hooks/use-online"
import { agencyInvitationsPath } from "@/routes"

interface InviteTeammateDialogProps {
  /** Trigger element passed via `<DialogTrigger asChild>`. */
  trigger: ReactNode
  /**
   * Optional ref forwarded for focus return on close. Not required when
   * the trigger is a child of a `<DialogTrigger>` ancestor (shadcn's
   * default flow handles that case).
   */
  triggerRef?: RefObject<HTMLElement | null>
}

/**
 * OKS-13 Path A — Agency invites a teammate (admin tier) to its agency.
 *
 * Single-field dialog: just `email`. Posts to `agencyInvitationsPath()`
 * with hidden `path: "member"` per BE TRD §API. Agency-tenant fan-out
 * (which agency this teammate joins) is server-derived from
 * `Current.user.agency_id` — no `agency_id` field on the wire.
 *
 * Tier policy (BE6): every member of an agency is `tenant_admin` in v1.
 * "Member" tier exists in the schema but is unused for agencies; the
 * UX surface treats every accepted teammate as an Admin row.
 *
 * Crew form rules: Inertia `<Form>` (NOT shadcn `Form`); plain `Input` +
 * `Label` + `<InputError>`; no react-hook-form; no zod. Email is auto-
 * canonicalised on blur (`trim().toLowerCase()`) so what the user sees
 * matches what the server stores.
 *
 * **Non-dismissable while submitting** — `<Dialog onOpenChange>` blocks
 * ESC + overlay-click while `processing`, matching UX Spec §States.
 *
 * Locked QA testid (OKS-13 v4 freeze):
 *   - `agency-invite-teammate-trigger` on the trigger element (consumer Button)
 *   - `agency-invite-teammate-dialog` on the DialogContent
 *   - `agency-invite-teammate-submit` on the primary button
 *   - `agency-invite-teammate-error-banner`
 *   - `agency-invite-teammate-offline-banner`
 */
export function InviteTeammateDialog({
  trigger,
  triggerRef,
}: InviteTeammateDialogProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState("")
  /**
   * Mid-submit guard for `onOpenChange`. Mirrors `processing` from the
   * Inertia `<Form>` render-prop on every render so ESC + overlay-click
   * can read the latest value outside the Form scope.
   */
  const submittingRef = useRef(false)

  function handleEmailChange(event: ChangeEvent<HTMLInputElement>) {
    setEmail(event.target.value)
  }

  function handleEmailBlur(event: FocusEvent<HTMLInputElement>) {
    const next = event.target.value.trim().toLowerCase()
    if (next !== email) setEmail(next)
  }

  function handleClose() {
    setOpen(false)
    setEmail("")
    triggerRef?.current?.focus()
  }

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && submittingRef.current) return
        if (!next) handleClose()
        else setOpen(true)
      }}
    >
      <ResponsiveDialogTrigger asChild>{trigger}</ResponsiveDialogTrigger>
      <ResponsiveDialogContent
        className="sm:max-w-md"
        data-testid="agency-invite-teammate-dialog"
      >
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>
            {t("agency.team.dialog.invite_teammate.title")}
          </ResponsiveDialogTitle>
          <ResponsiveDialogDescription className="sr-only">
            {t("agency.team.dialog.invite_teammate.description")}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        {/* OKS-120 — Form acts as scrollable body in mobile Sheet mode. */}
        <Form
          action={agencyInvitationsPath()}
          method="post"
          options={{ preserveScroll: true }}
          onSuccess={handleClose}
          resetOnSuccess
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 sm:p-0"
        >
          {({ processing, errors }) => {
            submittingRef.current = processing
            return (
              <>
                {/* Path discriminator — backend's single agency endpoint. */}
                <input type="hidden" name="path" value="member" />

                {errors.base && (
                  <div
                    role="alert"
                    className="border-destructive/40 bg-destructive/10 text-foreground rounded-md border px-3 py-2 text-sm"
                    data-testid="agency-invite-teammate-error-banner"
                  >
                    {Array.isArray(errors.base)
                      ? errors.base.join(" ")
                      : errors.base}
                  </div>
                )}
                {!online && (
                  <div
                    className="border-muted-foreground/20 bg-muted/40 text-muted-foreground rounded-md border px-3 py-2 text-sm"
                    data-testid="agency-invite-teammate-offline-banner"
                  >
                    {t("agency.team.dialog.offline_banner")}
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  <Label htmlFor="agency-invite-teammate-email">
                    {t("agency.team.dialog.invite_teammate.email_label")}
                    <span
                      className="text-destructive ml-0.5"
                      aria-hidden="true"
                    >
                      *
                    </span>
                  </Label>
                  <Input
                    id="agency-invite-teammate-email"
                    name="email"
                    type="email"
                    autoComplete="off"
                    autoFocus
                    aria-required="true"
                    value={email}
                    onChange={handleEmailChange}
                    onBlur={handleEmailBlur}
                    placeholder={t(
                      "agency.team.dialog.invite_teammate.email_placeholder",
                    )}
                    aria-invalid={errors.email ? "true" : undefined}
                    aria-describedby={
                      errors.email
                        ? "agency-invite-teammate-email-error"
                        : undefined
                    }
                    disabled={!online || processing}
                  />
                  <InputError
                    messages={errors.email}
                    id="agency-invite-teammate-email-error"
                  />
                </div>

                <ResponsiveDialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={processing}
                    aria-disabled={processing ? "true" : undefined}
                  >
                    {t("agency.team.dialog.invite_teammate.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    disabled={processing || !online}
                    aria-disabled={processing || !online ? "true" : undefined}
                    data-testid="agency-invite-teammate-submit"
                  >
                    {processing && (
                      <Loader2
                        className="size-4 animate-spin"
                        aria-hidden="true"
                      />
                    )}
                    {processing
                      ? t("agency.team.dialog.invite_teammate.sending")
                      : t("agency.team.dialog.invite_teammate.submit")}
                  </Button>
                </ResponsiveDialogFooter>
              </>
            )
          }}
        </Form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}
